import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { PendingSource } from "../lib/pendingGeneration";

const READING_LABELS = {
  url: "Reading your article",
  youtube: "Watching your video",
  text: "Reading your notes",
  pdf: "Reading your PDF",
} as const;
const PLANNING_STEPS = ["Pulling the strongest angles", "Planning your posts"];
// Reading usually takes a second; hold it long enough to be read.
const MIN_READING_MS = 6_000;
const ANGLES_MS = 20_000;
// Posts usually land within two minutes, so only past that is the wait unusual.
const LONG_WAIT_MS = 120_000;

/**
 * Fills a source row until the server has planned its posts. One wide panel
 * rather than per-post placeholders: how many posts come back isn't known yet.
 * ContentGallery swaps it for the real post cards as soon as they exist.
 *
 * The steps follow the server: nothing past reading shows until it reports
 * creating posts. Within that phase the angles/planning steps advance on a
 * timer and stop at the last one, so the copy never loops back.
 */
export function PendingGenerationTile({
  source,
  startedAt,
  stage,
}: {
  source: PendingSource;
  startedAt: number;
  stage?: "reading_sources" | "creating_posts";
}) {
  const [now, setNow] = useState(() => Date.now());
  // When this page first saw the server move on from reading.
  const [creatingSince, setCreatingSince] = useState<number | null>(() =>
    stage === "creating_posts" ? Date.now() : null,
  );

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (stage === "creating_posts")
      setCreatingSince((current) => current ?? Date.now());
  }, [stage]);

  const steps = [READING_LABELS[source.type] ?? "Reading your source", ...PLANNING_STEPS];
  const planningFrom =
    creatingSince === null
      ? null
      : Math.max(creatingSince, startedAt + MIN_READING_MS);
  const current =
    planningFrom === null || now < planningFrom
      ? 0
      : now < planningFrom + ANGLES_MS
        ? 1
        : 2;
  const isLongWait = now - startedAt >= LONG_WAIT_MS;

  return (
    <div
      role="status"
      aria-label={`Making posts from ${source.label}`}
      className="relative min-w-0 overflow-hidden rounded-xl bg-muted shadow-sm"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-background/60 to-transparent motion-safe:animate-shimmer [background-size:200%_100%]" />
      <div className="relative flex min-h-[9rem] flex-col justify-center px-5 py-6 sm:px-8">
        {/* Only steps reached so far; later ones don't exist yet. */}
        <ul className="space-y-2.5">
          {steps.slice(0, current + 1).map((label, index) => {
            const done = index < current;
            return (
              <li
                key={label}
                className={cn(
                  "flex items-center gap-2.5 text-sm motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-500",
                  done ? "text-muted-foreground" : "font-medium text-foreground",
                )}
              >
                {done ? (
                  <Check className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                ) : (
                  <Loader2 className="h-4 w-4 shrink-0 motion-safe:animate-spin text-primary/70" aria-hidden="true" />
                )}
                {done ? label : `${label}…`}
              </li>
            );
          })}
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          {isLongWait
            ? "This is taking longer than usual. Keep this page open to see your result."
            : "Usually ready in a minute or two."}
        </p>
      </div>
    </div>
  );
}
